/* global G */
import { curry, isStr } from 'lib/util'
import session from 'app/_shared/session'
import sequenceComponentFindProxy from 'lib/sequence/component/children/find'
import { get } from 'lib/sequence/component/state/value'
import csv from 'app/_shared/events/export/csv'
import { redirect } from 'app/_shared/events'

/**
 * Pads `x` with a 0 if it's less than 10
 *
 * @param {string|number} x
 * @returns {string|*}
 */
const padded = x => (x.toString().length === 1 ? `0${x}` : x)

/**
 * Creates the filename for the CSV
 *
 * @param {string|null} customer
 * @returns {`cart_${string}_${number}${string}${string}_${string}${string}${string}`}
 * @private
 */
const _getFileName = (customer) => {
  const date = new Date()

  if (!customer) {
    throw new ReferenceError('Customer is not defined')
  }

  return `cart_${customer}_${date.getFullYear()}${padded(date.getMonth() + 1)}${padded(date.getDay())}_${padded(date.getHours())}${padded(date.getMinutes())}${padded(date.getSeconds())}`
}

/**
 * Instructs the attachment adapter to get the CSV using `data` as `filename`.
 *
 * @type {(function(...[*]): (*))|*}
 * @private
 */
const _getCsv = curry(async ({ data, filename }, module, component, event) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  // Creating the file
  const file = await csv(module, component, { detail: { data, options: { filename, title: filename, delimiter: ';' } } })

  // Downloading the file using attachment adapter
  eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.LOAD), {
    [G.DATA]: {
      item: {
        value: file,
        name: filename,
      },
    },
  })
})

/**
 * Export CSV Or Redirect Event Handler
 *
 * Looks up the user's sales organizations, and if there is only one, immediately
 * calls {@link _getCsv} with it. If not, it'll perform a redirect.
 * @type {(function(...[*]): (*))|*}
 */
export const exportCsvOrRedirect = curry(async (module, component, event) => {
  const { user } = session(module)
  const userDoc = user?.ref()
  const salesOrganisations = userDoc?.value?.custom?.user?.combinations || []

  const model = module[G.MODEL]
  const modelCache = model[G.CACHE]
  const { value: { positions = [] } = {} } = modelCache || {}

  if (salesOrganisations?.length === 1) {
    const firstSalesOrg = salesOrganisations[0]
    const customerNumber = firstSalesOrg && isStr(firstSalesOrg)
      ? firstSalesOrg
      : firstSalesOrg?.customerNumber
    const filename = _getFileName(customerNumber)
    const data = positions.map(pos => ({
      amount: pos.amount,
      articleNumber: pos.articleNumber,
      comment: pos.comment,
    }))

    await _getCsv({ data, filename }, module, component, event)
  } else {
    await redirect(module, component, event)
  }
})

/**
 * Creates a CSV from the current cart and downloads it.
 *
 * @param {Gaia.AppModule.Spec} module        module composition
 * @param {Gaia.Component.Spec} component     action component
 * @param {Gaia.AppModule.EventHandler} event triggered event
 * @returns {Promise<void>}
 */
const exportCsv = async (module, component, event) => {
  const { user } = session(module)
  const actionComponent = module[G.STATE][G.ACTION][G.COMPONENT]
  const model = module[G.MODEL]
  const modelCache = model[G.CACHE]
  const { value: { positions = [] } = {} } = modelCache || {}

  const userDoc = user?.ref()
  const salesOrganisations = userDoc?.value?.custom?.user?.combinations || []

  const { salesOrganisation } = sequenceComponentFindProxy(actionComponent)
  const salesOrganisationIndex = get(salesOrganisation)
  const targetSalesOrganisation = salesOrganisations[salesOrganisationIndex] || null

  if (targetSalesOrganisation) {
    const customerNumber = targetSalesOrganisation && isStr(targetSalesOrganisation)
      ? targetSalesOrganisation
      : targetSalesOrganisation?.customerNumber
    const filename = _getFileName(customerNumber)
    const data = positions.map(pos => ({
      amount: pos.amount,
      articleNumber: pos.articleNumber,
      comment: pos.comment,
    }))

    await _getCsv({ data, filename }, module, component, event)
  }
}

export default curry(exportCsv)
